import { useCallback, useState } from 'react';
import useRunInDeferredTab from 'hooks/useRunInDeferredTab';
import { DEFAULT_PREPARING_CLIP_TAB_MESSAGE } from '../../../constants';
import { useClipSelect } from '../../ClipSelectContext';
import { ClipSelectDetailCardCTAIntent } from '../types';

export type UseCTAFlowProps = {
  cancelReprocess: () => void;
  reprocessNow: () => Promise<void>;
  isPendingReprocess: () => boolean;
  isPreviewDirty: () => boolean;
  suggestionId: number;
  onExportStart?: () => void;
};

/**
 * Manages the export logic and the CTAs in the detail view (edit or share).
 */
export default function useCTAFlow({
  cancelReprocess,
  isPendingReprocess,
  isPreviewDirty,
  onExportStart,
  reprocessNow,
  suggestionId,
}: UseCTAFlowProps) {
  const {
    exportClip,
    isExporting,
    isGeneratingPreview,
    prepareAdvancedEditorUrl,
    prepareShareUrl,
    pauseAll,
    suggestion,
  } = useClipSelect({
    clipId: suggestionId,
  });

  const runInTab = useRunInDeferredTab(DEFAULT_PREPARING_CLIP_TAB_MESSAGE);

  const [prevIsExporting, setPrevIsExporting] = useState(isExporting);

  const [ctaIntent, setCTAIntent] = useState<
    ClipSelectDetailCardCTAIntent | undefined
  >(undefined);

  const [showLoadingOverlay, setShowLoadingOverlay] = useState(
    isGeneratingPreview,
  );

  const handleCloseOverlay = useCallback(() => {
    setShowLoadingOverlay(false);
    setCTAIntent(undefined);
  }, []);

  const handleSubmit = useCallback(() => {
    pauseAll();
    setCTAIntent('share');

    if (suggestion.isVideoOutdated || isPendingReprocess()) {
      cancelReprocess();
      // overlay will allow user to choose what to do next
      exportClip();
    } else {
      runInTab(prepareShareUrl);
    }
  }, [
    cancelReprocess,
    exportClip,
    isPendingReprocess,
    prepareShareUrl,
    pauseAll,
    runInTab,
    suggestion.isVideoOutdated,
  ]);

  const handleOpenAdvancedEditor = useCallback(() => {
    pauseAll();
    setCTAIntent('edit');

    return runInTab(async () => {
      if (isPreviewDirty()) {
        cancelReprocess();
        await reprocessNow();
      }

      return prepareAdvancedEditorUrl();
    });
  }, [
    cancelReprocess,
    isPreviewDirty,
    prepareAdvancedEditorUrl,
    pauseAll,
    reprocessNow,
    runInTab,
  ]);

  if (isExporting !== prevIsExporting) {
    setPrevIsExporting(isExporting);

    if (isExporting) {
      setShowLoadingOverlay(true);
      onExportStart?.();
    }
  }

  return {
    ctaIntent,
    showLoadingOverlay,
    handleCloseOverlay,
    handleSubmit,
    handleOpenAdvancedEditor,
  };
}
